<template>
    <div class="Part Chart Item" :class="{loading: componentData.loading}">
        <h3 class="h3" v-if="item.title">{{ this.item.title }}</h3>
        <div class="loader-inline" v-if="componentData.loading"></div>
        <apexchart
            v-else
            :height="item.height ?? (isMobile ? 200 : 500)"
            :type="item.bar"
            :options="options"
            :series="series" />
    </div>
</template>

<script>
import {defineComponent,  reactive, watch, inject, computed, onMounted, onUnmounted } from "vue";

// External components
import Icon from "components/icon";
import trans from "helpers/_translation";
import { isMobile } from "helpers/_device";

import {getDataWithoutLimit} from "helpers/_api";
import { generalFilters, resolveFilters, resolveAppFilters } from "store/filters";
import { user_id } from "store/portal";

// Config for API urls used in this vue template
import { config, apiHostname } from "config";

import {
    date_filters,
    startDate,
    endDate,
    columnItems,
    columnTitles
} from "store/statistics";

import {getOptions} from "config/charts";

export default defineComponent({
	name: "workflowchart-item",
	components: {
        Icon
	},
	props: {
		item: {
			type: Object,
			required: true
		}
	},
	setup(props) {

        const viewData = inject("viewData");
        const componentData = reactive({
            loading: true,
            all: []
        });


        const filterByDate = (items, start, end) => {
            return items?.filter(item => {
                const targetDate = new Date(item.creation_date);
                if (start && (targetDate <= start)) {
                    return false;
                }
                if (end && (targetDate > end)) {
                    return false;
                }
                return true;
            }) ?? [];
        };

        const currentItems = (index, start = startDate.value, end = endDate.value) => {
            return computed(() => {
                // Based on .creation_date filter if within range
                return filterByDate(componentData.all[index], start, end);
            }).value;
        };

        const resolveProperties = (items, property_id) => {
            if (!property_id) {
                return [];
            }
            return items.map(item => {
                return item.extra.workflowproperties.find(a => `${a.field.id}` === `${property_id}`);
            }).filter(a => a);
        };
        const currentProperties = (index, start, end) => {
            return computed(() => {
                // Get all our properties for the current items
                return resolveProperties(currentItems(index, start, end), property_id(index));
            }).value;
        };

        const getData = () => {
            if (!user_id.value) return;
            if (!props.item) return;
            componentData.loading = true;

            const allPromises = [];
            resolveAppFilters(viewData.extra_filters).then(extra_filters => {
                const defaultFilters = [
                    [
                        "FieldFilter",
                        {
                            "column": "state",
                            "value": "Online"
                        }
                    ],
                    ...generalFilters.workflowusergroup,
                    ...extra_filters,
                    ...date_filters("creation_date")
                ];
                if (viewData.workflowboard_id) {
                    defaultFilters.push(...generalFilters.workflowboard[viewData.workflowboard_id]);
                }
                props.item.columns?.forEach((column, index) => {
                    // Set column specific filters
                    const tmpFilters = JSON.parse(JSON.stringify(defaultFilters)).filter(a => a);
                    tmpFilters.push(...column.filters);
                    if (!tmpFilters) return;

                    // Define our data array
                    componentData.all[index] = [];
                    // Now get our data
                    allPromises.push(new Promise(resolve => {
                        getDataWithoutLimit(
                            `${apiHostname}/api/v1.0/workflowcard?limit=101`,
                            resolveFilters(tmpFilters),
                            componentData.all,
                            index
                        ).then(resolve);
                    }));
                });
                Promise.all(allPromises).then(() => {
                    componentData.loading = false;
                });
            });
        };

        onMounted(() => {
            getData();
            watch(
                () => [user_id.value, props.item, startDate, endDate],
                (values) => {
                    if (!user_id.value) return;
                    // @TODO, instead of getting all items
                    // @TODO Get specific properties based on card result by filters passed
                    getData();
                },
                {
                    deep: true
                }
            )
        });



        const property_id = (index) => {
            return computed(() => {
                if (props.item.columns.some(column => !column?.property_id)) {
                    return undefined;
                }
                return props.item.columns?.[index]?.property_id;
            }).value;
        };
        const count = (index) => {
            return computed(() => {
                return currentItems(index).length;
            }).value;
        };
        const total = (index, start, end) => {
            return computed(() => {
                // Based on props.item.property_id, sum the values
                if (!property_id(index)) {
                    return currentItems(index, start, end).length;
                }
                // Otherwise sum up the values of the properties
                return currentProperties(index, start, end).reduce((a, b) => {
                    const value = b?.value.replaceAll(".", "").replace(",", ".");
                    return a + parseFloat(value);
                }, 0);
            }).value;
        };


        const options = computed(() => {
            const result = getOptions("default");
            result.xaxis.categories = columnTitles.value;
            return result;
        });
        const series = computed(() => {
            const result = [];
            props.item.columns?.forEach((column, index) => {
                const data = columnItems.value.map(item => {
                    return total(index, item.start, item.end);
                });
                result.push({
                    name: column.title,
                    data: data
                });
            });

            return result;
        });

		return {
            isMobile,

            trans,
            componentData,

            startDate,
            endDate,

            count,
            total,

            currentProperties,

            options,
            series
		};
	},
});

</script>
